/**
 * This decorator will cache the function result based on its parameters and return it on next call with the same parameters.
 * It means that the function will be called only once for each unique combination of parameters and the result will be cached.
 * The next calls with the same parameters will return the cached result.
 * It's good for functions that return consistent results for the same inputs but have expensive computations.
 *
 * IMPORTANT: In TypeScript, decorators can only be applied to class methods, properties, or parameters.
 * They CANNOT be applied directly to standalone functions.
 *
 * Example for class instance methods:
 * ```ts
 * class MyClass {
 *   @Cached()
 *   expensiveCalculation(param1: string, param2: number) {
 *     // complex computation that depends on parameters
 *     return result;
 *   }
 * }
 * ```
 *
 * Example for static class methods (recommended for utility functions):
 * ```ts
 * class Utils {
 *   @Cached()
 *   static expensiveCalculation(param1: string, param2: number) {
 *     // complex computation that depends on parameters
 *     return result;
 *   }
 * }
 * ```
 *
 * INCORRECT usage (will cause TypeScript errors):
 * ```ts
 * @Cached() // This will cause a TypeScript error: "Decorators are not valid here"
 * export function myFunction(param1: string, param2: number) {
 *   // complex computation
 *   return result;
 * }
 * ```
 */

/**
 * Creates a caching decorator that can be applied to class methods (instance or static).
 * @returns A decorator function that caches results based on input parameters
 */
export function Cached(): MethodDecorator & PropertyDecorator {
	const cacheMap = new WeakMap<any, Map<string, any>>();

	return function (target: any, propertyKey: string | symbol, descriptor?: PropertyDescriptor): any {
		// Check if this is a class method (descriptor will be defined)
		// or a property (descriptor will be undefined)
		const isClassMethod = descriptor !== undefined;

		if (isClassMethod && descriptor) {
			const originalMethod = descriptor.value;
			descriptor.value = function (...args: any[]) {
				// Create a cache for this instance if it doesn't exist
				let cache = cacheMap.get(this);
				if (!cache) {
					cache = new Map<string, any>();
					cacheMap.set(this, cache);
				}

				// Create a unique key based on method name and arguments
				const cacheKey = `${String(propertyKey)}_${JSON.stringify(args)}`;

				// Return cached value if exists
				if (cache.has(cacheKey)) {
					return cache.get(cacheKey);
				}

				// Compute, cache and return the result
				const result = originalMethod.apply(this, args);
				cache.set(cacheKey, result);
				return result;
			};
			return descriptor;
		} else {
			// This branch is for compatibility with older code and should not be used for new code
			// NOTE: This approach doesn't work with modern TypeScript and will cause errors
			// Use class static methods instead as shown in the documentation
			const originalFunction = target[propertyKey];

			target[propertyKey] = function (...args: any[]) {
				// Use a global cache for standalone functions
				let cache = cacheMap.get(target);
				if (!cache) {
					cache = new Map<string, any>();
					cacheMap.set(target, cache);
				}

				// Create a unique key based on function name and arguments
				const cacheKey = `${String(propertyKey)}_${JSON.stringify(args)}`;

				// Return cached value if exists
				if (cache.has(cacheKey)) {
					return cache.get(cacheKey);
				}

				// Compute, cache and return the result
				const result = originalFunction.apply(this, args);
				cache.set(cacheKey, result);
				return result;
			};

			return target;
		}
	};
}
